import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import Fuse from 'fuse.js'
import { patientService, PatientSearchParams, extractPatients } from '../services/patientService'
import { Patient } from '../types/patient'
import { formatPatientName } from '../utils/name'
import { formatDate } from '../utils/locale'
import { QuickAddPatient } from './QuickAddPatient'
import './PatientSearch.css'

interface PatientSearchProps {
  value?: string // Selected patient ID
  onSelect: (patientId: string) => void
  placeholder?: string
  required?: boolean
}

interface AdvancedSearchFilters {
  firstName?: string
  lastName?: string
  phone?: string
  email?: string
  patientNumber?: string
  ppsNumber?: string
  gmsNumber?: string
  dateOfBirth?: string
}

export function PatientSearch({ value, onSelect, placeholder = 'Search for patient...', required = false }: PatientSearchProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [showQuickAdd, setShowQuickAdd] = useState(false)
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedSearchFilters>({})
  const [includeInactive, setIncludeInactive] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [searchParams, setSearchParams] = useState<PatientSearchParams | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)

  // Build structured search parameters from search term and advanced filters
  const buildSearchParams = useMemo((): PatientSearchParams | null => {
    const hasSearchTerm = searchTerm.trim().length > 0
    const hasFilters = Object.values(advancedFilters).some((v) => v)
    
    if (!hasSearchTerm && !hasFilters) {
      return null
    }
    
    const params: PatientSearchParams = {}
    
    if (hasSearchTerm) {
      params.search = searchTerm.trim()
    }
    
    if (advancedFilters.firstName) params.firstName = advancedFilters.firstName
    if (advancedFilters.lastName) params.lastName = advancedFilters.lastName
    if (advancedFilters.phone) params.phone = advancedFilters.phone
    if (advancedFilters.email) params.email = advancedFilters.email
    if (advancedFilters.patientNumber) params.patientNumber = advancedFilters.patientNumber
    if (advancedFilters.ppsNumber) params.ppsNumber = advancedFilters.ppsNumber
    if (advancedFilters.gmsNumber) params.gmsNumber = advancedFilters.gmsNumber
    if (advancedFilters.dateOfBirth) params.dateOfBirth = advancedFilters.dateOfBirth
    if (includeInactive) params.includeInactive = true
    
    return params
  }, [searchTerm, advancedFilters, includeInactive])

  // Fetch patients with server-side search - only when explicitly triggered
  const { data: allPatients, isLoading } = useQuery({
    queryKey: ['patients', 'search', searchParams],
    queryFn: async () => {
      const result = await patientService.getAll(searchParams!)
      // Handle both array and paginated response for backward compatibility
      return extractPatients(result)
    },
    enabled: searchParams !== null, // Only enabled when search is explicitly triggered
  })

  // Filter and apply fuzzy matching to patients
  // Note: Backend now handles the filtering based on includeInactive parameter
  // This frontend filter is just for additional client-side filtering if needed
  const filteredPatients = useMemo(() => {
    if (!allPatients || !Array.isArray(allPatients)) return []

    // Backend already filtered based on includeInactive, so we just need to exclude archived
    let filtered = allPatients.filter((p: Patient) => !p.isArchived)

    // Date of birth filter (handled on backend now, but keep for safety)
    if (advancedFilters.dateOfBirth) {
      filtered = filtered.filter((p: Patient) => p.dateOfBirth === advancedFilters.dateOfBirth)
    }

    // Apply fuzzy matching if we have a search term
    if (searchTerm.trim() && filtered.length > 0) {
      const fuseOptions = {
        threshold: 0.4, // Lower = stricter (0 = exact match, 1 = match anything)
        keys: [
          'firstName',
          'lastName',
          'middleName',
          'email',
          'phone',
          'patientNumber',
          'ppsNumber',
          'gmsNumber',
        ],
        includeScore: true,
      }

      const fuse = new Fuse(filtered, fuseOptions)
      const fuzzyResults = fuse.search(searchTerm.trim())
      
      // Return top 50 matches, sorted by relevance score
      filtered = fuzzyResults
        .slice(0, 50)
        .map(result => result.item)
    }

    return filtered.slice(0, 50) // Limit to 50 results for performance
  }, [allPatients, advancedFilters.dateOfBirth, searchTerm])

  // Fetch selected patient separately if needed (when value is set but not in current results)
  const { data: selectedPatientData } = useQuery({
    queryKey: ['patients', value],
    queryFn: () => patientService.getById(value!),
    enabled: !!value && !allPatients?.some((p: Patient) => p.id === value),
  })

  const selectedPatient = allPatients?.find((p: Patient) => p.id === value) || selectedPatientData

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleSearch = () => {
    const params = buildSearchParams
    if (params) {
      setSearchParams(params)
      setHasSearched(true)
      setShowResults(true)
    } else {
      // No search criteria, clear results
      setSearchParams(null)
      setHasSearched(false)
      setShowResults(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearch()
    }
  }

  const handlePatientSelect = (patientId: string) => {
    onSelect(patientId)
    setShowResults(false)
    setSearchTerm('')
    setAdvancedFilters({})
    setSearchParams(null)
    setHasSearched(false)
  }

  const handleClear = () => {
    setSearchTerm('')
    setAdvancedFilters({})
    setShowResults(false)
    setSearchParams(null)
    setHasSearched(false)
    onSelect('')
  }

  const handleQuickAddPatient = (patientId: string) => {
    // Select the patient first - ensure this happens synchronously
    onSelect(patientId)
    // Close modal immediately - don't wait for state updates
    setShowQuickAdd(false)
    setShowResults(false)
    setSearchTerm('')
  }

  const handleAdvancedFilterChange = (key: keyof AdvancedSearchFilters, value: string) => {
    setAdvancedFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }))
  }

  const hasActiveFilters = Object.values(advancedFilters).some((v) => v)

  return (
    <div className="patient-search">
      {/* Hidden input for form validation - holds the actual patientId value */}
      {required && (
        <input
          type="text"
          name="patientId"
          id="patientId"
          value={value || ''}
          required={required}
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, width: 0, margin: 0, padding: 0, border: 'none' }}
          tabIndex={-1}
          aria-hidden="true"
          readOnly
        />
      )}
      <div className="patient-search-input-wrapper">
        <div className="search-input-container">
          <input
            ref={searchInputRef}
            type="text"
            className="patient-search-input"
            placeholder={selectedPatient ? formatPatientName(selectedPatient as any) : placeholder}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (hasSearched) {
                setShowResults(true)
              }
            }}
          />
          {selectedPatient && (
            <button
              type="button"
              className="clear-selection"
              onClick={handleClear}
              aria-label="Clear selection"
            >
              ×
            </button>
          )}
        </div>
        <button
          type="button"
          className={`advanced-toggle ${showAdvanced ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
          onClick={() => setShowAdvanced(!showAdvanced)}
          title="Advanced search"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M2 4h12M4 8h8M6 12h4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          {hasActiveFilters && <span className="filter-indicator">•</span>}
        </button>
        <button
          type="button"
          className="search-button"
          onClick={handleSearch}
          disabled={isLoading || (!searchTerm.trim() && !hasActiveFilters)}
          title="Search for patients"
        >
          {isLoading ? (
            <svg className="spinner" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" strokeDasharray="43.98" strokeDashoffset="10" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M7 13A6 6 0 1 0 7 1a6 6 0 0 0 0 12zM15 15l-4.35-4.35"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          Search
        </button>
      </div>

      <div className="patient-search-actions">
        <button
          type="button"
          className="quick-add-button"
          onClick={() => setShowQuickAdd(true)}
          title="Quick Add New Patient"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M8 3v10M3 8h10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Quick Add Patient
        </button>
      </div>

      {showAdvanced && (
        <div className="advanced-search-panel">
          <div className="advanced-search-row">
            <div className="advanced-search-field">
              <label>First Name</label>
              <input
                type="text"
                value={advancedFilters.firstName || ''}
                onChange={(e) => handleAdvancedFilterChange('firstName', e.target.value)}
                placeholder="First name"
              />
            </div>
            <div className="advanced-search-field">
              <label>Last Name</label>
              <input
                type="text"
                value={advancedFilters.lastName || ''}
                onChange={(e) => handleAdvancedFilterChange('lastName', e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>
          <div className="advanced-search-row">
            <div className="advanced-search-field">
              <label>Phone</label>
              <input
                type="text"
                value={advancedFilters.phone || ''}
                onChange={(e) => handleAdvancedFilterChange('phone', e.target.value)}
                placeholder="Phone number"
              />
            </div>
            <div className="advanced-search-field">
              <label>Email</label>
              <input
                type="email"
                value={advancedFilters.email || ''}
                onChange={(e) => handleAdvancedFilterChange('email', e.target.value)}
                placeholder="Email address"
              />
            </div>
          </div>
          <div className="advanced-search-row">
            <div className="advanced-search-field">
              <label>Patient Number</label>
              <input
                type="text"
                value={advancedFilters.patientNumber || ''}
                onChange={(e) => handleAdvancedFilterChange('patientNumber', e.target.value)}
                placeholder="Patient number"
              />
            </div>
            <div className="advanced-search-field">
              <label>PPS Number</label>
              <input
                type="text"
                value={advancedFilters.ppsNumber || ''}
                onChange={(e) => handleAdvancedFilterChange('ppsNumber', e.target.value)}
                placeholder="PPS number"
              />
            </div>
          </div>
          <div className="advanced-search-row">
            <div className="advanced-search-field">
              <label>GMS Number</label>
              <input
                type="text"
                value={advancedFilters.gmsNumber || ''}
                onChange={(e) => handleAdvancedFilterChange('gmsNumber', e.target.value)}
                placeholder="GMS number"
              />
            </div>
            <div className="advanced-search-field">
              <label>Date of Birth</label>
              <input
                type="date"
                value={advancedFilters.dateOfBirth || ''}
                onChange={(e) => handleAdvancedFilterChange('dateOfBirth', e.target.value)}
                placeholder="Date of birth"
              />
            </div>
          </div>
          <div className="advanced-search-row">
            <div className="advanced-search-field">
              <label>
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  aria-label="Include inactive patients"
                />
                <span>Include inactive patients</span>
              </label>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="advanced-search-actions">
              <button
                type="button"
                className="btn-clear-filters"
                onClick={() => {
                  setAdvancedFilters({})
                }}
              >
                Clear Advanced Filters
              </button>
            </div>
          )}
        </div>
      )}

      {showResults && hasSearched && (
        <div ref={resultsRef} className="patient-search-results">
          {isLoading ? (
            <div className="search-results-loading">Loading patients...</div>
          ) : filteredPatients.length === 0 ? (
            <div className="search-results-empty">
              <div>No patients found matching your search criteria</div>
              <button
                type="button"
                className="quick-add-from-empty"
                onClick={() => setShowQuickAdd(true)}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 3v10M3 8h10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Quick Add New Patient
              </button>
            </div>
          ) : (
            <>
              <div className="search-results-header">
                {filteredPatients.length} {filteredPatients.length === 1 ? 'patient' : 'patients'} found
              </div>
              <div className="search-results-list">
                {filteredPatients.map((patient) => (
                  <button
                    key={patient.id}
                    type="button"
                    className={`patient-result-item ${value === patient.id ? 'selected' : ''}`}
                    onClick={() => handlePatientSelect(patient.id)}
                  >
                    <div className="patient-result-name">
                      {formatPatientName(patient as any)}
                      {(patient.isPrimary === false || patient.primaryPatientId) && (
                        <span className="linked-status-badge secondary" title="Secondary record - read-only">
                          Secondary (read-only)
                        </span>
                      )}
                      {(patient.isPrimary === true || (!patient.isPrimary && !patient.primaryPatientId)) && 
                       (patient.linkedPatientIds && patient.linkedPatientIds.length > 0) && (
                        <span className="linked-status-badge primary" title="Primary record with linked records">
                          Primary ({patient.linkedPatientIds.length})
                        </span>
                      )}
                    </div>
                    <div className="patient-result-details">
                      {patient.dateOfBirth && (
                        <span>DOB: {formatDate(patient.dateOfBirth)}</span>
                      )}
                      {patient.phone && (
                        <span>{patient.dateOfBirth ? ' • ' : ''}{patient.phone}</span>
                      )}
                    </div>
                    <div className="patient-result-identifiers">
                      {patient.patientNumber && (
                        <span className="patient-result-badge">#{patient.patientNumber}</span>
                      )}
                      {patient.ppsNumber && (
                        <span>{patient.patientNumber ? ' • ' : ''}PPS: {patient.ppsNumber}</span>
                      )}
                      {patient.gmsNumber && (
                        <span>{patient.patientNumber || patient.ppsNumber ? ' • ' : ''}GMS: {patient.gmsNumber}</span>
                      )}
                      {!patient.patientNumber && !patient.ppsNumber && !patient.gmsNumber && (
                        <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>No identifiers</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {selectedPatient && !showResults && (
        <div className="selected-patient-info">
          <div className="selected-patient-details">
            <strong>{formatPatientName(selectedPatient as any)}</strong>
            {selectedPatient.dateOfBirth && (
              <span> • DOB: {formatDate(selectedPatient.dateOfBirth)}</span>
            )}
            {selectedPatient.patientNumber && (
              <span> • #{selectedPatient.patientNumber}</span>
            )}
            {selectedPatient.ppsNumber && (
              <span> • PPS: {selectedPatient.ppsNumber}</span>
            )}
            {selectedPatient.gmsNumber && (
              <span> • GMS: {selectedPatient.gmsNumber}</span>
            )}
          </div>
        </div>
      )}

      {showQuickAdd && (
        <div 
          className="quick-add-patient-overlay" 
          onClick={(e) => {
            // Only close if clicking directly on overlay, not on modal content
            if (e.target === e.currentTarget) {
              setShowQuickAdd(false)
            }
          }}
        >
          <div 
            className="quick-add-patient-modal" 
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <QuickAddPatient
              onPatientCreated={handleQuickAddPatient}
              onCancel={() => setShowQuickAdd(false)}
            />
          </div>
        </div>
      )}
    </div>
  )
}
